import { useState } from 'react';
import { User, LogOut, Crown, Settings, GitCompare, BarChart3, HelpCircle, Menu, X, SlidersHorizontal, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from './AuthModal';
import { AccountSettings } from './AccountSettings';
import { PreferencesModal } from './PreferencesModal';

interface NavbarProps {
  onLogoClick?: () => void;
  onCompareClick?: () => void;
  onBenchmarkClick?: () => void;
  onFaqClick?: () => void;
  onContactClick?: () => void;
}

export function Navbar({ onLogoClick, onCompareClick, onBenchmarkClick, onFaqClick, onContactClick }: NavbarProps) {
  const { user, profile, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const getTierLabel = () => {
    const tier = profile?.subscriptionTier || 'free';
    const tierLabels: { [key: string]: string } = {
      'free': 'Free',
      'starter': 'Starter',
      'professional': 'Professional',
      'business': 'Business',
      'enterprise': 'Enterprise',
    };
    return tierLabels[tier] || 'Free';
  };

  const isPaidTier = profile?.subscriptionTier && profile.subscriptionTier !== 'free';

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setShowMobileMenu(false);
  };

  const closeMobileMenu = () => setShowMobileMenu(false);

  return (
    <>
      <nav className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={onLogoClick}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-2xl">owl</span>
              </div>
              <span className="text-xl font-medium text-gray-900">Apex Analyst</span>
            </button>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-4">
              {onFaqClick && (
                <button
                  onClick={onFaqClick}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  FAQ
                </button>
              )}
              <button
                onClick={() => setShowPreferences(true)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black transition-colors"
                aria-label="Open preferences"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Preferences
              </button>
              {user && (
                <>
                  {onCompareClick && (
                    <button
                      onClick={onCompareClick}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black transition-colors"
                    >
                      <GitCompare className="w-4 h-4" />
                      Compare
                    </button>
                  )}
                  {onBenchmarkClick && (
                    <button
                      onClick={onBenchmarkClick}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Benchmark
                    </button>
                  )}
                </>
              )}
              {onContactClick && (
                <button
                  onClick={onContactClick}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact Us
                </button>
              )}
            </div>

            {/* Desktop auth / user menu */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <span className="text-sm text-gray-700">{user.email}</span>
                    {isPaidTier && <Crown className="w-4 h-4 text-yellow-500" />}
                  </button>

                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                        <div className="p-3 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900">{user.email}</p>
                          <p className="text-xs text-gray-500 mt-1">{getTierLabel()} Plan</p>
                          {profile?.subscriptionTier && ['business', 'enterprise'].includes(profile.subscriptionTier) && (
                            <p className="text-xs text-green-600 mt-1">Unlimited analyses</p>
                          )}
                          {profile?.subscriptionTier && !['business', 'enterprise'].includes(profile.subscriptionTier) && (
                            <p className="text-xs text-gray-500 mt-1">{profile.analysisCount || 0} analyses used</p>
                          )}
                        </div>
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                          onClick={() => { setShowUserMenu(false); setShowAccountSettings(true); }}
                        >
                          <Settings className="w-4 h-4" />
                          Account Settings
                        </button>
                        {!isPaidTier && (
                          <button
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                            onClick={() => { setShowUserMenu(false); window.location.href = '/?pricing=true'; }}
                          >
                            <Crown className="w-4 h-4 text-yellow-500" />
                            Upgrade Plan
                          </button>
                        )}
                        <button
                          onClick={() => { signOut(); setShowUserMenu(false); }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="text-sm font-medium text-gray-700 hover:text-black transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className="bg-black text-white text-sm font-medium py-2 px-6 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {showMobileMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={closeMobileMenu} />
            <div className="relative z-20 border-t border-gray-200 bg-white px-4 py-4 space-y-1 md:hidden">
              {onFaqClick && (
                <button
                  onClick={() => { onFaqClick(); closeMobileMenu(); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  FAQ
                </button>
              )}
              {onContactClick && (
                <button
                  onClick={() => { onContactClick(); closeMobileMenu(); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact Us
                </button>
              )}
              {user && (
                <>
                  {onCompareClick && (
                    <button
                      onClick={() => { onCompareClick(); closeMobileMenu(); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <GitCompare className="w-4 h-4" />
                      Compare
                    </button>
                  )}
                  {onBenchmarkClick && (
                    <button
                      onClick={() => { onBenchmarkClick(); closeMobileMenu(); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Benchmark
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-2" />
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{getTierLabel()} Plan</p>
                  </div>
                  <button
                    onClick={() => { setShowAccountSettings(true); closeMobileMenu(); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Account Settings
                  </button>
                  {!isPaidTier && (
                    <button
                      onClick={() => { window.location.href = '/?pricing=true'; closeMobileMenu(); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Crown className="w-4 h-4 text-yellow-500" />
                      Upgrade Plan
                    </button>
                  )}
                  <button
                    onClick={() => { signOut(); closeMobileMenu(); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </>
              )}
              <button
                onClick={() => { setShowPreferences(true); closeMobileMenu(); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Preferences
              </button>
              {!user && (
                <>
                  <button
                    onClick={() => handleAuthClick('login')}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => handleAuthClick('signup')}
                    className="w-full bg-black text-white text-sm font-medium py-3 px-3 rounded-lg hover:bg-gray-800 transition-colors text-center"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
      />

      <PreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
      />
    </>
  );
}
